/**
 * Lightweight reading progress indicator for article pages.
 *
 * Calculates scroll progress based on the article body container
 * (`[data-reading-progress-target]`) and updates a fixed top progress bar - add the appropriate HTML structure to your article ftlh template.
 * @see src/main/resources/templates/themes/zorka/article.ftlh
 *
 * add div.article-reading-progress template.ftlh
 * @see src/main/resources/templates/themes/zorka/common/template.ftlh
 * (`[data-article-reading-progress]`) using CSS transform.
 *
 * add initArticleReadingProgress(); to onArticle.ts hook
 *
 * Uses `requestAnimationFrame` throttling for smooth scroll/resize updates.
 * Does NOT use React state — pure DOM manipulation for performance.
 */
export function initArticleReadingProgress() {
    const root = document.querySelector('[data-article-reading-progress]');
    const target = document.querySelector('[data-reading-progress-target]');
    if (!root || !target) {
        return;
    }
    // Guard against repeated initialisation
    if (root.getAttribute('data-initialized') === 'true') {
        return;
    }
    root.setAttribute('data-initialized', 'true');
    const bar = root.querySelector('.article-reading-progress__bar');
    if (!bar) {
        return;
    }
    let ticking = false;
    function updateProgress() {
        const start = target.offsetTop;
        const end = start + target.offsetHeight - window.innerHeight;
        const scrollTop = window.scrollY;
        let progress;
        if (end <= start) {
            // Article body is shorter than the viewport
            progress = scrollTop >= start ? 1 : 0;
        }
        else {
            progress = (scrollTop - start) / (end - start);
        }
        progress = Math.max(0, Math.min(1, progress));
        // Update bar width via CSS transform (GPU-accelerated)
        bar.style.transform = `scaleX(${progress})`;
        // Update ARIA attribute for accessibility
        root.setAttribute('aria-valuenow', String(Math.round(progress * 100)));
    }
    function onScrollOrResize() {
        if (!ticking) {
            requestAnimationFrame(() => {
                updateProgress();
                ticking = false;
            });
            ticking = true;
        }
    }
    window.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize, { passive: true });
    // Initial calculation
    updateProgress();
}
